<script lang="ts" setup>
// @ts-ignore
import RouteStops from "@/views/trips/view/components/RouteStops.vue";
// @ts-ignore
import RouteInfo from "@/views/trips/view/components/RouteInfo.vue";
import type { TripDto } from "@/api/trips/dto/trip.dto";
import { computed, onMounted, ref } from "vue";
import { RouteStatus } from "@/constants/trip";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import TruckRoutesDialog from "@/components/truck/TruckRoutesDialog.vue";
import { TRUCK_ID } from "@/constants/query";
import type { TruckRouteWithTruckNumber } from "@/api/trucks/dto/truck.dto";

const props = defineProps<{
  trip: TripDto;
}>();

const emit = defineEmits(["change"]);

const api = new API();

const showTruckRoutesDialog = ref<boolean>(false);
const routesWithEmptyMiles = ref<TruckRouteWithTruckNumber[]>([]);
const location = useRoute();
const router = useRouter();
const { t } = useI18n();

const truckIdsFromQuery = computed(() => {
  const ids = location.query?.[TRUCK_ID] || [];

  return (Array.isArray(ids) ? ids : [ids]) as string[];
});

async function getEmptyMilesRoutes() {
  try {
    const promises = truckIdsFromQuery.value
      .filter((id): id is string => id !== undefined)
      .map((truckId) =>
        api.trucks.getTruckRoutes(truckId, [RouteStatus.notStarted, RouteStatus.inProgress, RouteStatus.planned]),
      );

    const responses = await Promise.all(promises);

    const allRoutes = responses
      .filter((res) => res.success && res.data?.routes)
      .flatMap((res) =>
        res.data?.routes.map((route) => {
          return {
            ...route,
            truckNumber: res.data?.truckNumber,
          };
        }),
      ) as TruckRouteWithTruckNumber[];

    routesWithEmptyMiles.value = allRoutes.filter((route) => route.wrongDeadheadDistance);

    if (routesWithEmptyMiles.value.length) {
      showTruckRoutesDialog.value = true;
    } else {
      clearTruckIdsFromQuery();
    }
  } catch (error) {
    console.error("Ошибка при загрузке маршрутов:", error);
  }
}

const emptyMilesRoutesTitle = computed(() => {
  const routeList = routesWithEmptyMiles.value.map((route) => `#${route.id}`).join(", ");

  if (!routeList.length) {
    return;
  }

  return `${t("general.route")} ${routeList} ${t("general.have").toLowerCase()} ${t("general.been-cancelled-or-changed").toLowerCase()}`;
});

function onChange() {
  emit("change");
}

function clearTruckIdsFromQuery() {
  const { [TRUCK_ID]: _, ...rest } = location.query;

  router.replace({
    name: location.name,
    params: location.params,
    query: rest,
  });
}

onMounted(async () => {
  await getEmptyMilesRoutes();
});
</script>

<template>
  <v-row class="mt-4">
    <v-col v-for="route in props.trip.routes" :key="'info-' + route.id" lg="6" xl="4">
      <route-info :route="route" :trip-id="props.trip.id" @change="onChange"></route-info>
      <route-stops :route="route" :trip="props.trip" @change="onChange"></route-stops>
    </v-col>
  </v-row>

  <truck-routes-dialog
    v-model="showTruckRoutesDialog"
    :routes="routesWithEmptyMiles"
    @on-close="clearTruckIdsFromQuery"
  >
    <template #header>
      <div class="text-center">
        <div class="font-size-20 font-weight-500 mb-4 mt-5">{{ emptyMilesRoutesTitle }}</div>

        <span class="text-grey-darken-1"
          >{{ t("general.it-may-affected-empty-miles-of-the-next-planned-route") }}, <br />
          <span>
            {{ t("general.please-recalculate-or-cancel") }}
          </span></span
        >
      </div>
    </template>
  </truck-routes-dialog>
</template>

<style lang="scss" scoped></style>
